import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, BackHandler, Platform, StyleSheet, TouchableHighlight, View } from 'react-native';
import PropTypes from 'prop-types';
import { useRecoilState, useRecoilValue } from 'recoil';

import { LiveControlsTestIDs, livePageTestIDs, menuTestIDs, Theme, VODControlsTestIDs } from '@constants';
import { useAppContextReference, useNodeHandle } from '@hooks';
import { navigationService } from '@services';
import { screenReaderEnabledState, vodPlaylistState } from '@store';

import { VODVideoControls } from './VODVideoControls/VODVideoControls';
import { ContextDrawer } from './ContextDrawer';
import { VideoControls } from './Controls';

import { DEBUG, isAndroid, isTizen, isWeb } from '../../../helpers/Platform';
import { FOCUS_NOW } from '../../../helpers/Tools';

const CONSTANTS = {
  animateInDuration: 250,
  animateOutDuration: 500,
  showControlsDuration: 5000,
};

const useTVEventHandler = require('react-native').useTVEventHandler || (() => null);

/**
 * Container for video player controls. Will animate in and out as needed on button presses.
 * Note- do NOT include this component when controls are not used, on PIP or in-line players for example.
 * Only to be used on full screen video playback.
 *
 * Params:
 * isLive- used to control showing live or VOD controls
 * data- pass needed data for displaying video title, progress, etc.
 * preventButtons- might need updating in the future, but will not show the controls when the specified button
 * is pressed. Empty by default (all buttons on the remote will show the controls).
 */
export const ControlsContainer = ({ isLive, nextFocusUp, data, onControl, preventButtons = [], controlsOnRef }) => {
  const fadeValue = useRef(new Animated.Value(0)).current;
  const [lastInput, setLastInput] = useState(null);
  const [isPlaylistFocused, setIsPlaylistFocused] = useState(false);

  const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);

  const showingControls = useRef(false);
  const controlsFocused = useRef(false);
  const containerFocused = useRef(false);
  const controlsReference = useRef();

  const preventAllButtons = preventButtons.includes('all');

  const liveFirstFocusReference = useAppContextReference(LiveControlsTestIDs.liveVideoControls);
  const vodFirstFocusReference = useAppContextReference(VODControlsTestIDs.vodVideoControls);

  const liveFirstFocusHandle = useNodeHandle(liveFirstFocusReference);
  const vodFirstFocusHandle = useNodeHandle(vodFirstFocusReference);

  const exitNodeReference = useAppContextReference(LiveControlsTestIDs.liveVideoControlsExit);

  const menuReference = useAppContextReference(menuTestIDs.mainMenu);
  const menuHandle = useNodeHandle(menuReference);

  const showControlsReference = useAppContextReference(LiveControlsTestIDs.showLiveVideoControls);

  const showMenuReference = useAppContextReference(menuTestIDs.showMainMenu);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const showControls = () => {
    if (!preventAllButtons) {
      setLastInput(Date.now());
      Animated.timing(fadeValue, {
        toValue: 1,
        duration: CONSTANTS.animateInDuration,
        useNativeDriver: true,
      }).start();

      if (!showingControls.current) {
        focusControls();
        showingControls.current = true;
        controlsFocused.current = true;
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hideControls = (refocus = true) => {
    Animated.timing(fadeValue, {
      toValue: 0,
      duration: CONSTANTS.animateOutDuration,
      useNativeDriver: true,
    }).start();
    showingControls.current = false;
    if (refocus && !preventAllButtons) {
      FOCUS_NOW(exitNodeReference);
    }
  };

  useEffect(() => {
    showControlsReference.current = {
      showControls,
      hideControls,
    };
  }, [showControlsReference, showControls, hideControls]);

  const focusControls = () => {
    const route = navigationService.getCurrentRoute();

    if (isLive && route === livePageTestIDs.livePage) {
      FOCUS_NOW(liveFirstFocusReference);
    } else {
      FOCUS_NOW(vodFirstFocusReference);
    }
  };

  const onBackPress = () => {
    if (showingControls.current && !preventAllButtons) {
      if (isPlaylistFocused && controlsReference.current) {
        controlsReference.current?.focusPlayButton();
        controlsReference.current?.hidePlayList();
        return true;
      }

      if (isLive) {
        // This was commented out to fix WEXF-847
        // Will need revisit on requirements for back button on live
        //showMenuReference.current?.hideMenu();
      }
      hideControls(true);
      return true;
    }

    return false;
  };

  const [currentVODPlaylistState, setVODPLaylist] = useRecoilState(vodPlaylistState);

  const { playlist, currentID } = currentVODPlaylistState ?? {};

  const goToNextVideo = () => {
    const index = playlist.findIndex(video => video._id === currentID);

    if (index >= 0 && index + 1 < playlist.length) {
      const nextID = playlist[index + 1]._id;

      navigationService.setParameters({ videoID: nextID }); // TODO: Set episode and season index?
      setVODPLaylist({
        ...currentVODPlaylistState,
        currentID: nextID,
      });
    } else {
      const currentParameters = navigationService.getCurrentRouteParameters();
      const { goNext } = currentParameters;

      navigationService.setParameters({
        ...navigationService.getCurrentRouteParameters(),
        startPosition: 0,
        goNext: goNext ? goNext + 1 : 1,
      });
    }
  };

  const goToPreviousVideo = () => {
    const index = playlist.findIndex(video => video._id === currentID);

    if (index >= 0 && index - 1 >= 0) {
      const previousID = playlist[index - 1]._id;

      navigationService.setParameters({ videoID: previousID }); // TODO: Set episode and season index?
      setVODPLaylist({
        ...currentVODPlaylistState,
        currentID: previousID,
      });
    } else {
      const currentParameters = navigationService.getCurrentRouteParameters();
      const { goPrevious } = currentParameters;

      navigationService.setParameters({
        ...navigationService.getCurrentRouteParameters(),
        goPrevious: goPrevious ? goPrevious + 1 : 1,
      });
    }
  };

  const eventHandler = event => {
    if (isWeb && !controlsFocused.current && !containerFocused.current) {
      return;
    }

    const action = event.eventKeyAction ?? 0;
    let eventType = event.eventType ?? event.key;
    const isMenu = event.isMenuEvent ?? false;

    const mediaControlKeycodes = {
      19: 'MediaPause',
      412: 'MediaRewind',
      417: 'MediaFastForward',
      415: 'MediaPlay',
      413: 'MediaStop',
      418: 'MediaTrackNext',
      419: 'MediaTrackPrevious',
    };

    if (event.keyCode && mediaControlKeycodes[event.keyCode]) {
      eventType = mediaControlKeycodes[event.keyCode];
    }

    // Can test in browser with `n` and `p` keys
    if (DEBUG) {
      if (event.key === 'n') {
        eventType = 'MediaTrackNext';
      }
      if (event.key === 'p') {
        eventType = 'MediaTrackPrevious';
      }
    }

    if (!isLive) {
      switch (eventType) {
        case 'MediaPause':
        case 'MediaPlay':
          onControl(VideoControls.TOGGLE_PAUSE);
          break;
        case 'MediaRewind':
          onControl(VideoControls.SEEK, data.progress * data.duration - 15);
          break;
        case 'MediaFastForward':
          onControl(VideoControls.SEEK, data.progress * data.duration + 15);
          break;
        case 'MediaStop':
          navigationService.goBack();
          break;
        case 'MediaTrackNext':
          goToNextVideo();
          break;
        case 'MediaTrackPrevious':
          goToPreviousVideo();
          break;
      }
    }

    if (
      action === 0 &&
      !isMenu &&
      eventType !== 'focus' &&
      eventType !== 'blur' &&
      !preventAllButtons &&
      !preventButtons.includes(eventType)
    ) {
      showControls();
    }
  };

  useTVEventHandler(eventHandler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    useCallback(() => {
      BackHandler.addEventListener('hardwareBackPress', onBackPress, 20);
      if (isWeb) {
        document.addEventListener('keydown', eventHandler);
      }
      return () => {
        if (isWeb) {
          document.removeEventListener('keydown', eventHandler);
        }
        BackHandler.removeEventListener('hardwareBackPress', onBackPress);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaylistFocused, preventAllButtons, data]),
  );

  useEffect(() => {
    const now = Date.now();
    const diff = now - lastInput;
    let hideTimer;

    if (showingControls.current && !screenReaderEnabled) {
      hideTimer = setTimeout(() => {
        hideControls();
      }, CONSTANTS.showControlsDuration - diff);
    }

    // clearTimeout when playlist drawer is focused.
    if (isPlaylistFocused) {
      clearTimeout(hideTimer);
    }

    return () => {
      clearTimeout(hideTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastInput, isPlaylistFocused, preventButtons, screenReaderEnabled]);

  return (
    <>
      <Animated.View
        style={[styles.background, { opacity: fadeValue }]}
        useNativeDriver
        onBlur={event => {}}
        onFocus={() => {
          // Will fire on web for child views
          controlsFocused.current = true;
        }}
      >
        {isLive ? (
          <ContextDrawer
            animateValue={fadeValue}
            data={data}
            nextFocusUp={menuHandle}
            showControls={() => {
              showControls();
              showMenuReference.current?.showMenu();
            }}
          />
        ) : (
          <VODVideoControls
            controlsOnRef={reference => {
              controlsReference.current = reference;
              controlsOnRef && controlsOnRef({ ...reference, hideControls });
            }}
            data={data}
            setIsPlaylistFocused={setIsPlaylistFocused}
            showControls={showControls}
            onControl={onControl}
          />
        )}
      </Animated.View>
      <TouchableHighlight
        key={'exit_controls'}
        activeOpacity={0}
        nextFocusDown={isLive ? liveFirstFocusHandle : vodFirstFocusHandle}
        nextFocusLeft={isLive ? liveFirstFocusHandle : vodFirstFocusHandle}
        nextFocusRight={isLive ? liveFirstFocusHandle : vodFirstFocusHandle}
        nextFocusUp={nextFocusUp ? nextFocusUp : isAndroid ? vodFirstFocusHandle : null}
        ref={exitNodeReference}
        style={styles.exitNode}
        testID={LiveControlsTestIDs.liveVideoControlsExit}
        underlayColor={'clear'}
        onBlur={() => (containerFocused.current = false)}
        onFocus={() => {
          containerFocused.current = true;
          if (!isPlaylistFocused) {
            hideControls(false);
          }
        }}
        onPress={event => {
          const eventType = event.eventType;
          const tvosPress = Platform.isTVOS && eventType === 'select';
          // Raw press event (no event type)- only fired once
          // Other press events are for keydown and keyup, may or may not be fired depending on platform

          if (!eventType || tvosPress) {
            showControls();
          }
        }}
      >
        <View />
      </TouchableHighlight>
    </>
  );
};

ControlsContainer.propTypes = {
  isLive: PropTypes.bool,
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  data: PropTypes.object,
  onControl: PropTypes.func,
  preventButtons: PropTypes.array,
  controlsOnRef: PropTypes.element,
};

ControlsContainer.defaultProps = {
  isLive: false,
  nextFocusUp: null,
  data: null,
  onControl: () => {},
  preventButtons: [],
  controlsOnRef: null,
};

const styles = StyleSheet.create({
  background: {
    backgroundColor: Theme.withOpacity(Theme.black, 0.8),
    elevation: 600,
    height: Theme.appHeight,
    left: 0,
    position: isWeb && !isTizen ? 'fixed' : 'absolute',
    top: 0,
    width: Theme.appWidth,
    zIndex: 600,
  },
  exitNode: {
    height: 1,
    width: 1,
  },
});
